import { AuthUser } from 'aws-amplify/auth'
import { Location } from 'history'
import moment from 'moment'
import { createSelector } from 'reselect'
import { PerSessionValuesType } from 'src/cookies'
import { ExtendLocales, UserStatus } from 'src/enums'
import { getLocaleInfo } from 'src/localization/locales'
import { ScreeningStatus } from 'src/models/virtualCard.models'
import { onboardingUrl } from 'src/navigation/urls'
import { RootState } from 'src/redux/createRootReducer'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import {
  selectDefaultCurrencyFromState,
  selectDefaultLocaleFromState,
} from 'src/redux/selectors/session.selectors'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { matchExtendLocale } from 'src/utils/string.utils'

export const selectEmailFromState = (state: RootState): string =>
  state.auth.email

export const selectJwtFromState = (state: RootState): string | undefined =>
  state.auth.jwt

export const selectRefreshTokenFromState = (
  state: RootState,
): string | undefined => state.auth.refreshToken

export const selectUserStatusFromState = (
  state: RootState,
): UserStatus | undefined => state.auth.userStatus

export const selectUserIdFromState = (state: RootState): string | undefined =>
  state.auth.userId

export const selectIsAuthenticatedFromState = (state: RootState): boolean =>
  state.auth.isAuthenticated

export const selectUserSessionTokenIdFromState = (
  state: RootState,
): string | undefined => state.auth.userSessionTokenId

export const selectCspJwtFromState = (state: RootState): string | undefined =>
  state.auth.cspJwt

export const selectCspRefreshTokenFromState = (
  state: RootState,
): string | undefined => state.auth.cspRefreshToken

export const selectIsCspAuthenticatedFromState = (state: RootState): boolean =>
  state.auth.isCspAuthenticated

export const selectCspUserIdFromState = (
  state: RootState,
): string | undefined => state.auth.cspUserId

export const selectStartTimeStampFromState = (
  state: RootState,
): moment.Moment | undefined => state.auth.startTimeStamp

export const selectCspIssuerIdsFromState = (
  state: RootState,
): string[] | undefined => state.auth.cspIssuerIds

export const selectIsRenewingAuthFromState = (state: RootState): boolean =>
  state.auth.isRenewingAuth

export const selectRenewAuthTopLevelErrorFromState = (
  state: RootState,
): string | undefined => state.auth.renewAuthTopLevelError

export const selectRenewAuthErrorDetailMapFromState = (
  state: RootState,
): ErrorDetailMap<string> | undefined => state.auth.renewAuthErrorDetailMap

export const selectAuthedRedirectLocationFromState = (
  state: RootState,
): Location => state.auth.authedRedirectLocation

export const selectDidSessionExpireAutomaticallyFromState = (
  state: RootState,
): boolean => state.auth.didSessionExpireAutomatically

export const selectAuthTypeFromState = (state: RootState): string | undefined =>
  state.auth.authType

export const selectFetchingAuthTypeFromState = (state: RootState): boolean =>
  state.auth.fetchingAuthType

export const selectFetchAuthTypeTopLevelErrorFromState = (
  state: RootState,
): string | undefined => state.auth.fetchAuthTypeTopLevelError

export const selectFetchAuthTypeErrorDetailMapFromState = (
  state: RootState,
): ErrorDetailMap<string> | undefined => state.auth.fetchAuthTypeErrorDetailMap

export const selectMfaChallengeInitiated = (state: RootState): boolean =>
  state.auth.mfaChallengeInitiated

export const selectMfaSentFromState = (state: RootState): boolean =>
  state.auth.mfaSent

export const selectMfaDestinationFromState = (
  state: RootState,
): string | undefined => state.auth.mfaDestination

export const selectMfaDeliveryMediumFromState = (
  state: RootState,
): string | undefined => state.auth.mfaDeliveryMedium

export const selectCognitoUserFromState = (
  state: RootState,
): AuthUser | undefined => state.auth.cognitoUser

export const selectFetchingCurrentDeviceKeyFromState = (
  state: RootState,
): boolean => state.auth.fetchingCurrentDeviceKey

export const selectDeviceKeyFromState = (
  state: RootState,
): string | undefined => state.auth.deviceKey

export const selectAcceptOrgInviteFromState = (
  state: RootState,
): boolean | undefined => state.auth.acceptOrgInvite

export const selectOrgInviteErrorFromState = (
  state: RootState,
): boolean | undefined => state.auth.orgInviteError

const safeDecodeJwt = (token: string | undefined): Record<string, any> | undefined => {
  // SSR cookie JWT may be a non-string (boolean, object) — guard against
  // calling .split() on undefined/non-string values during hydration.
  if (!token || typeof token !== 'string') return undefined
  try {
    return JSON.parse(window.atob(token.split('.')[1]))
  } catch (e) {
    // eslint-disable-next-line no-console
    if (typeof console !== 'undefined') console.warn('Failed to decode JWT:', e)
    return undefined
  }
}

export const selectDecodedJwt = createSelector([selectJwtFromState], safeDecodeJwt)

export const selectDecodedCspJwt = createSelector(
  [selectCspJwtFromState],
  safeDecodeJwt,
)

export const selectDefaultCspIssuerId = createSelector(
  [selectCspIssuerIdsFromState],
  (issuerIds) =>
    issuerIds !== undefined && issuerIds.length > 0 ? issuerIds[0] : undefined,
)

export const selectUserListModelFromState = createSelector(
  [selectUserIdFromState, selectUserListModelsFromState],
  (id, users) => (id !== undefined ? users[id] : undefined),
)

export const selectCspUserListModelFromState = createSelector(
  [selectCspUserIdFromState, selectUserListModelsFromState],
  (id, users) => (id !== undefined ? users[id] : undefined),
)

export const selectUnauthedLocaleFromState = (
  state: RootState,
): ExtendLocales | undefined => state.auth.unauthedLocale

export const selectNullSafeLocaleFromState = createSelector(
  [
    selectUserListModelFromState,
    selectCspUserListModelFromState,
    selectDefaultLocaleFromState,
    selectCalculatedBackgroundLocation,
    selectIsAuthenticatedFromState,
    selectQueryParams,
    selectUnauthedLocaleFromState,
  ],
  (user, cspUser, locale, bgLocation, authed, queryParams, unauthedLocale) => {
    const isOnboarding = !authed || bgLocation.pathname === onboardingUrl()

    if (isOnboarding) {
      if (queryParams.locale !== undefined)
        return matchExtendLocale(queryParams.locale)
      if (unauthedLocale !== undefined) return unauthedLocale
      return window.navigator.language as ExtendLocales
    }

    return user?.locale || cspUser?.locale || locale
  },
)

export const selectNullSafeTimezoneFromState = createSelector(
  [selectUserListModelFromState, selectCspUserListModelFromState],
  (user, cspUser) => user?.timezone || cspUser?.timezone || 'America/New_York',
)

export const selectNullSafeCurrencyFromState = createSelector(
  [
    selectUserListModelFromState,
    selectCspUserListModelFromState,
    selectDefaultCurrencyFromState,
  ],
  (user, cspUser, currency) => user?.currency || cspUser?.currency || currency,
)

export const selectNullSafeCurrencyFromLocale = createSelector(
  [selectNullSafeLocaleFromState],
  (locale) => getLocaleInfo(locale).currency,
)

export const selectUserHasForbiddenScreening = createSelector(
  [selectUserListModelFromState],
  (user) =>
    user?.issuerSanctions?.some(
      (issuerSanction) => issuerSanction.status === ScreeningStatus.Forbidden,
    ),
)

export const selectUserHasFailScreening = createSelector(
  [selectUserListModelFromState],
  (user) =>
    user?.issuerSanctions?.some(
      (issuerSanction) => issuerSanction.status === ScreeningStatus.Fail,
    ),
)

export const selectPerSessionValues = (
  state: RootState,
): PerSessionValuesType | undefined => state.auth.perSessionValues

export const selectIsOrglessUserWarningDismissed = (
  state: RootState,
): boolean | undefined =>
  state.auth.perSessionValues?.isOrglessUserWarningDismissed

export const selectIsBudgetWarningDismissed = (
  state: RootState,
): boolean | undefined => state.auth.perSessionValues?.isBudgetWarningDismissed

export const selectIsOrglessGuestCardWarningDismissed = (
  state: RootState,
): boolean | undefined =>
  state.auth.perSessionValues?.isOrglessGuestCardWarningDismissed

export const selectIsOrgAdminUserWarningDismissed = (
  state: RootState,
): boolean | undefined =>
  state.auth.perSessionValues?.isOrgAdminUserWarningDismissed

export const selectIsConfirmOrgModalAccountAccountsDismissed = (
  state: RootState,
): boolean | undefined =>
  state.auth.perSessionValues?.isConfirmOrgModalAccountAccountsDismissed

export const selectIsConfirmRCNOrBudgetModalAccountOverviewDismissed = (
  state: RootState,
): boolean | undefined =>
  state.auth.perSessionValues?.isConfirmRCNOrBudgetModalAccountOverviewDismissed

export const selectIsConfirmBudgetModalAccountsBudgetsDismissed = (
  state: RootState,
): boolean | undefined =>
  state.auth.perSessionValues?.isConfirmBudgetModalAccountsBudgetsDismissed

export const selectIsConfirmBudgetModalCreateBudgetDismissed = (
  state: RootState,
): boolean | undefined =>
  state.auth.perSessionValues?.isConfirmBudgetModalCreateBudgetDismissed

export const selectIsConfirmBudgetModalUpdateBudgetDismissed = (
  state: RootState,
): boolean | undefined =>
  state.auth.perSessionValues?.isConfirmBudgetModalUpdateBudgetDismissed

export const selectIsAcceptOrgInvitePopupDismissed = (
  state: RootState,
): boolean | undefined =>
  state.auth.perSessionValues?.isAcceptOrgInvitePopupDismissed

export const selectIsUserOrglessUser = createSelector(
  [selectUserListModelFromState],
  (user) => user !== undefined && !user?.isInExplicitOrg,
)

export const selectHideWelcomeBackBannerFromState = (
  state: RootState,
): boolean | undefined => state.auth.hideWelcomeBackBanner

export const selectVerifyPhoneTopLevelErrorFromState = (
  state: RootState,
): string | undefined => state.auth.verifyPhoneTopLevelError

export const selectResendVerificationTopLevelErrorFromState = (
  state: RootState,
): string | undefined => state.auth.resendVerificationTopLevelError

export const selectVerifyPhoneVerificationLoadingFromState = (
  state: RootState,
): boolean | undefined => state.auth.verifyPhoneVerificationLoading

export const selectSendPhoneVerificationLoadingFromState = (
  state: RootState,
): boolean | undefined => state.auth.sendPhoneVerificationLoading

export const selectPhoneNumberResponseValueFromState = (
  state: RootState,
): string | undefined => state.auth.getPhoneNumberResponseValue

export const selectUserEmailVerificationStatusFromState = (
  state: RootState,
): string | undefined => state.auth.userEmailVerificationStatus

export const selectVerificationPhoneResentFromState = (
  state: RootState,
): boolean | undefined => state.auth.verificationPhoneResent
